/**
 * ETF price fetcher - calls our /api/etf-price proxy.
 *
 * The proxy runs on Vercel Edge and tries multiple upstream providers
 * (Yahoo v8 -> Stooq) server-side. Frontend just makes one call.
 *
 * In local dev, Vite proxies /api/* to the deployed Vercel URL so the same
 * code works without needing `vercel dev`.
 *
 * If all upstream providers fail, the proxy returns a 502. The user can
 * then enter a manual price via the Purchases tab.
 *
 * Patch BQ: added a 12-second wall-clock timeout via AbortController.
 * The edge function has its own 5-second-per-provider timeouts (so a
 * worst-case chain of 4 providers caps at 20s), but if the edge
 * function itself hangs entirely (cold start that doesn't return, or
 * Vercel infrastructure issue), the client used to wait forever and
 * lock the calling action's state. 12s is just over two provider
 * timeouts plus some slack, which is the realistic wall-clock budget
 * for "we tried hard, give up and let the user manually retry."
 */
import { Decimal } from '../../engine/money.js';

const ETF_PRICE_PROXY = '/api/etf-price';
const CLIENT_TIMEOUT_MS = 12_000;

export interface EtfPriceSnapshot {
  priceUsd: Decimal;
  fetchedAt: Date;
  /** Which upstream actually succeeded - useful for debugging. */
  source?: string;
}

interface ProxyResponse {
  symbol: string;
  priceUsd: number;
  source: string;
  fetchedAtMs: number;
}

interface ProxyError {
  error: string;
  details?: string[];
}

export async function fetchEtfPrice(symbol: string): Promise<EtfPriceSnapshot> {
  // Patch BQ: AbortController guards against the proxy hanging entirely.
  // Edge function has its own per-provider timeouts so this is a safety
  // net for the rare case where the function itself is unresponsive.
  const controller = new AbortController();
  const timer = setTimeout(() => controller.abort(), CLIENT_TIMEOUT_MS);
  try {
    const res = await fetch(`${ETF_PRICE_PROXY}?symbol=${encodeURIComponent(symbol)}`, {
      headers: { Accept: 'application/json' },
      signal: controller.signal,
    });
    if (!res.ok) {
      let msg = `ETF proxy: HTTP ${res.status}`;
      try {
        const body = (await res.json()) as ProxyError;
        if (body?.error) msg = body.error;
        if (body?.details?.length) msg += ` (${body.details.join(' / ')})`;
      } catch { /* ignore */ }
      throw new Error(msg);
    }
    const data = (await res.json()) as ProxyResponse;
    if (typeof data.priceUsd !== 'number' || !isFinite(data.priceUsd) || data.priceUsd <= 0) {
      throw new Error(`ETF proxy: invalid price ${data.priceUsd} for ${symbol}`);
    }
    return {
      priceUsd: new Decimal(data.priceUsd),
      fetchedAt: new Date(data.fetchedAtMs),
      source: data.source,
    };
  } catch (e) {
    if (controller.signal.aborted) {
      throw new Error(`ETF proxy: client timeout after ${CLIENT_TIMEOUT_MS}ms for ${symbol}`);
    }
    throw e;
  } finally {
    clearTimeout(timer);
  }
}
